import axios from 'axios';
import qs from 'qs';

export default {
    name: 'login-form',
    props: {
        isMobile: {
            type: Boolean,
            default: false,
        },
        alert: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            username: '',
            password: '',
            buttonClasses: ['is-fullwidth'],
            loadingState: false,
        };
    },
    computed: {
        // Enable button if username & password have a value
        isCompleted() {
            return this.username && this.password;
        },
    },
    // Methods needed for this component.
    methods: {
        // Submit request for v2 login.
        v2Login(data) {
            return axios.post('/login_handler.php', data, {
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
            });
        },
        // Submit request for v3 login.
        v3Login(data) {
            return axios.post(`${process.env.VUE_APP_QV3_API}/login`, data);
        },
        // Submit request for v4 login.
        v4Login(data) {
            return axios.post(
                `${process.env.VUE_APP_QV4_API}/users/login`,
                data,
            );
        },
        // Submit form event.
        onSubmit() {
            // Add the loading spinner icon to the button.
            this.loadingState = true;

            // Reset any alerts previously displayed.
            this.$emit('currentAlert', null);

            // Add the loading spinner icon to the button.
            this.buttonClasses.push('button-loading');

            // Set data object(s) for ajax requests.
            // gsData is formatted as form data rather than json.
            let data = {
                    username: this.username,
                    password: this.password,
                    login: 'Sign In',
                },
                qsData = qs.stringify(data);

            Promise.allSettled([
                this.v2Login(qsData),
                this.v3Login(data),
                this.v4Login(data),
            ]).then(([v2Result, v3Result, v4Result]) => {
                // Check for forced password change

                if (
                    v4Result.status === 'fulfilled' &&
                    typeof v4Result.value.data !== 'undefined' &&
                    v4Result.value.data.requiresPasswordChange
                ) {
                    this.loadingState = false;
                    let user = {
                        username: data.username,
                        userId: v4Result.value.data.userId,
                    };
                    let token = v4Result.value.data.token;
                    this.$emit('userInfo', user);
                    this.$emit('authToken', token);
                    this.$emit('currentView', 'force-password-change');
                    this.$emit(
                        'currentTransition',
                        'component-slide-fade-left',
                    );
                    return;
                }
                // Helper to get status code from result
                function getStatus(result) {
                    if (result.status === 'fulfilled') {
                        return result.value && result.value.status;
                    } else if (
                        result.status === 'rejected' &&
                        result.reason &&
                        result.reason.response
                    ) {
                        return result.reason.response.status;
                    }
                    return null;
                }

                const v2Status = getStatus(v2Result);
                const v3Status = getStatus(v3Result);
                const v4Status = getStatus(v4Result);

                const v2Failed = v2Status !== 200;
                const v3Failed = v3Status !== 200;
                const v4Failed = v4Status !== 200;

                const v2Data =
                    v2Result.status === 'fulfilled' ? v2Result.value : null;
                const v2Error =
                    v2Result.status === 'rejected' ? v2Result.reason : null;

                // Debug: log status of each call
                // console.log(
                //     'v2Failed:',
                //     v2Failed,
                //     'v3Failed:',
                //     v3Failed,
                //     'v4Failed:',
                //     v4Failed,
                // );
                // console.log(
                //     'v2Status:',
                //     v2Status,
                //     'v3Status:',
                //     v3Status,
                //     'v4Status:',
                //     v4Status,
                // );
                // console.log('v2Result:', v2Result);
                // console.log('v3Result:', v3Result);
                // console.log('v4Result:', v4Result);

                if (v3Failed || v4Failed || v2Failed) {
                    // Remove loading spinner on button
                    this.loadingState = false;

                    let logoutUrl = this.isMobile
                        ? '/mobile/logout.php'
                        : '/logout.php';

                    axios.post(logoutUrl);

                    // Set alert data.
                    let alert = {
                            message: this.$t('HC_FAILED_LOGIN'),
                            status: 'danger',
                            icon: 'exclamation-circle-outline',
                        },
                        index = this.buttonClasses.indexOf('button-loading');

                    // Remove loading spinner icon from button.
                    if (index > -1) {
                        this.buttonClasses.splice(index, 1);
                    }

                    // If they've attempted 9 times, this is their 10th, so send to locked page.
                    // Try to get status from v3 or v4 error
                    let status = null;

                    if (
                        v2Failed &&
                        v2Result.reason &&
                        v2Result.reason.response
                    ) {
                        status = v2Result.reason.response.status;
                    } else if (
                        v3Failed &&
                        v3Result.reason &&
                        v3Result.reason.response
                    ) {
                        status = v3Result.reason.response.status;
                    } else if (
                        v4Failed &&
                        v4Result.reason &&
                        v4Result.reason.response
                    ) {
                        status = v4Result.reason.response.status;
                    }

                    if (status === 429) {
                        this.$emit('currentView', 'locked');
                        this.$emit(
                            'currentTransition',
                            'component-slide-fade-up',
                        );
                        this.$emit('currentAlert', null);
                    } else {
                        // If v2 failed, show its error message if available
                        if (v2Failed) {
                            let v2Message = null;
                            if (
                                v2Result.status === 'rejected' &&
                                v2Error &&
                                v2Error.response &&
                                v2Error.response.data &&
                                v2Error.response.data.message
                            ) {
                                v2Message = v2Error.response.data.message;
                            } else if (
                                v2Result.status === 'fulfilled' &&
                                v2Data &&
                                v2Data.data &&
                                v2Data.data.message
                            ) {
                                v2Message = v2Data.data.message;
                            }
                            if (v2Message) {
                                alert.message = v2Message;
                            }
                        }
                        // Debug: log alert before emitting

                        this.$emit('currentAlert', alert);
                    }
                    return;
                }

                // All succeeded
                const v3Data = v3Result.value;
                const v4Data = v4Result.value;

                // Reset login attempts with successful login
                window.localStorage.setItem('locked', false);

                // For impersonation, remove @ when storing username in localStorage
                data.username =
                    data.username.charAt(0) == '@'
                        ? data.username.substr(1)
                        : data.username;

                // Create authenticated object which is used on some v3 api calls
                let auth = {
                    user: {
                        id: v4Data.data.userId,
                        username: data.username,
                    },
                    v3: {
                        url: process.env.VUE_APP_QV3_API,
                        token: v3Data.data.data.token,
                    },
                    v4: {
                        url: process.env.VUE_APP_QV4_API,
                        token: v4Data.data.token,
                    },
                };

                // Set authenticated in browser local storage.
                window.localStorage.setItem(
                    'authenticated',
                    JSON.stringify(auth),
                );

                if (v4Data.data.signedJWT) {
                    var token = v4Data.data.signedJWT;
                    var domain = process.env.VUE_APP_COOKIE_DOMAIN;
                    var d = new Date();
                    d.setTime(d.getTime() + 8 * 24 * 60 * 60 * 1000); // Expires in 8 days
                    var expires = 'expires=' + d.toUTCString();
                    document.cookie =
                        'QV2Auth=' +
                        token +
                        '; ' +
                        expires +
                        '; domain=' +
                        domain +
                        '; path=/';
                }

                // Remove the loading spinner icon from button, and show success icon.
                let index = this.buttonClasses.indexOf('button-loading');
                let redirectUrl;

                if (index > -1) {
                    this.buttonClasses.splice(index, 1);
                }

                this.buttonClasses.push('button-success');
                // If they are a corporate user.
                if (v4Data.data.agreementId && !this.isMobile) {
                    redirectUrl = '/corporateportal/reports.php';

                    // If there is a redirect param in the url.
                } else if (this.$parent.redirect) {
                    redirectUrl = '/' + this.$parent.redirect;

                    // If it's mobile.
                } else if (this.isMobile) {
                    redirectUrl = '/mobile/profile.php';

                    // Anything else.
                } else {
                    redirectUrl = '/dashboard';
                }

                // Redirect to dashboard after successful login.
                window.location.href = redirectUrl;
            });
        }, // end onSubmit,
        // Handle button state based on username and password being populated.
        onChangeInputField() {
            // Validate Username
            let validUsername = this.username;

            // Validate Password
            let validPassword = this.password;

            this.isCompleted = validUsername && validPassword;
        },
        // Click event for the forgot password link.
        onClickForgotPassword(e) {
            e.preventDefault();
            this.$emit('currentView', 'forgot-password');
            this.$emit('currentTransition', 'component-slide-fade-up');
            this.$emit('currentAlert', null);
        },
        // Transition to Support component on Forgot Quore ID click.
        onClickForgotQuoreID(e) {
            e.preventDefault();
            this.$emit('currentView', 'support');
            this.$emit('currentStatus', 'quoreid');
            this.$emit('currentTransition', 'component-slide-fade-up');
            this.$emit('currentAlert', null);
        },
    },
};
